import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { useWebAuthn } from 'react-hook-webauthn';
import Cookies from "js-cookie";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { authenticationService } from '../services/authenticationService';
import { Role } from '../app/role';
import { LoadingPanel } from "./LoadingPanel";
import { addNotificationWithTimeout } from '../app/addNotificationWithTimeout';

import {
    FormInput,
} from "./forms/form-components";

import './Login.scss';

export const Login = (props) => {
    const rpOptions = {
        rpId: window.location.hostname,
        rpName: "OTT",
    }

    const dispatch = useDispatch();
    const { getAssertion } = useWebAuthn(rpOptions);

    const TwoFactorAuthenticationType = {
        Disabled: 0,
        Authenticator: 1,
        Email: 2,
    }

    const emailRegex = new RegExp(/\S+@\S+\.\S+/);

    const currentUser = authenticationService.currentUserValue;
    const cookieEmailAddress = Cookies.get("EmailAddress");

    const [isLoading, setIsLoading] = useState(false);
    const [showTwoFactorAuthenticationForm, setShowTwoFactorAuthenticationForm] = useState(false);
    const [loginKey, setLoginKey] = useState(undefined);
    const [isTwoFactorAuthenticationSetUp, setIsTwoFactorAuthenticationSetUp] = useState(false);
    const [qrCodeSetupImageUrl, setQrCodeSetupImageUrl] = useState(undefined);
    const [twoFactorAuthenticationTypeId, setTwoFactorAuthenticationTypeId] = useState(undefined);
    const [redirectPath, setRedirectPath] = useState(undefined);
    const [initialLoginFormValues] = useState({
        emailAddress: cookieEmailAddress,
    });
    const [enteredEmailAddress, setEnteredEmailAddress] = useState(cookieEmailAddress);

    const emailAddressChanged = (event) => {
        setEnteredEmailAddress(event.target.value);
    }

    const isLoggedIn = () => {
        if (!currentUser) {
            return false;
        }

        switch (currentUser.role) {
            case Role.Administrator:
            case Role.User:
                return true;

			default:
				return false;
        }
    }

    const getRedirectPath = () => {
        switch (currentUser.role) {
            case Role.Administrator:
                return "/administrator";

            case Role.User:
                return "/user";

			default:
				return "/";
        }
    }

    const requiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        return "";
    }

    const emailValidator = (value) => {
        if (emailRegex.test(value) === false) {
            return "This field is required";
        }

        if (value.length > 100) {
            return "The maximum length is 100 characters";
        }

        return "";
    }

    const passwordValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 50) {
            return "The maximum length is 50 characters";
        }

        return "";
    }

    const loginFormSubmitted = (dataItem) => {        
        setIsLoading(true);

        const loginPromise = authenticationService.login(dataItem.emailAddress, dataItem.password);

        loginPromise
            .then((result) => {                
                setLoginKey(result.loginKey);
                setTwoFactorAuthenticationTypeId(result.twoFactorAuthenticationTypeId);
                setShowTwoFactorAuthenticationForm(true); 

                if (result.twoFactorAuthenticationTypeId === TwoFactorAuthenticationType.Authenticator) {
                    setIsTwoFactorAuthenticationSetUp(result.isTwoFactorAuthenticationSetUp);
                    setQrCodeSetupImageUrl(result.qrCodeSetupImageUrl);
                }
                else if (result.twoFactorAuthenticationTypeId === TwoFactorAuthenticationType.Email) {
                    setIsTwoFactorAuthenticationSetUp(true);
                }
                else if (result.twoFactorAuthenticationTypeId === TwoFactorAuthenticationType.Disabled) {
                    setShowTwoFactorAuthenticationForm(false); 
                    setIsTwoFactorAuthenticationSetUp(true);

                    if (redirectPath && redirectPath !== "/login") {
                        window.location = redirectPath;
                        return;
                    }

                    window.location = "/";   
                }
                
                setIsLoading(false);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                setIsLoading(false);
            });
    }

    const twoFactorAuthenticationFormSubmitted = (dataItem) => {
        setIsLoading(true);

        const loginPromise = authenticationService.validateTwoFactorAuthenticationCode(loginKey, dataItem.twoFactorAuthenticationCode);

        loginPromise
            .then(() => {     
                if (redirectPath && redirectPath !== "/login") {
                    window.location = redirectPath;
                    return;
                }

                window.location = "/";            
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                setIsLoading(false);
            });
    }

    const validateLoginKey = useCallback(async () => {
        if (emailRegex.test(enteredEmailAddress) === false) {
            addNotificationWithTimeout(dispatch, "error", "Email address is required");
            return;
        }

        if (enteredEmailAddress.length > 100) {
            addNotificationWithTimeout(dispatch, "error", "The maximum email address length is 100 characters");
            return;
        }

        setIsLoading(true);

        const assertion = await getAssertion({ challenge: 'stringFromServer' });

        const publicKey = assertion.id;

        const loginPromise = authenticationService.loginWithKey(enteredEmailAddress, publicKey);

        loginPromise
            .then(() => {
                if (redirectPath && redirectPath !== "/login") {
                    window.location = redirectPath;
                    return;
                }

                window.location = "/";
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                setIsLoading(false);
            });
    }, [getAssertion]);

    useEffect(() => {  
        const baseTitle = "OTT";

        document.title = "Login";

        if (baseTitle !== "") {
            document.title += ` | ${baseTitle}`;
        }

        setRedirectPath(props.location.state.from);
    }, []);

    useEffect(() => {
        if (showTwoFactorAuthenticationForm) {
            const element = document.querySelector(".twoFactorAuthenticationCode");

            if (element) {
                element.focus();
            }
        }        
    }, [showTwoFactorAuthenticationForm]);

    return (
        <>
            {isLoggedIn() ? <Redirect to={getRedirectPath()} /> :                
                (
                    <Dialog className="login">
                        {isLoading && <LoadingPanel />}

                        <h1 className="logo">
                            OTT
                        </h1>

                        {!showTwoFactorAuthenticationForm ?
                            <Form
                                onSubmit={loginFormSubmitted}
                                initialValues={initialLoginFormValues}
                                render={(formRenderProps) => (
                                    <FormElement>                                    
                                        <fieldset className={"k-form-fieldset"}>  
                                            <Field
                                                id={"emailAddress"}
                                                name={"emailAddress"}
                                                label="Email address"
                                                type={"email"}
                                                component={FormInput}
                                                validator={emailValidator}
                                                autoComplete="username"
                                                aria-label="Email address"
                                                onChange={emailAddressChanged}
                                            />

                                            <Field
                                                id={"password"}
                                                name={"password"}
                                                label="Password"
                                                component={FormInput}
                                                validator={passwordValidator}
                                                type={"password"}
                                                showPasswordToggle={true}
                                                autoComplete="current-password"
                                                aria-label="Password"
                                            />
                                        </fieldset>
                                        <div className="k-form-buttons">
                                            <Button
                                                themeColor={"primary"}
                                                type={"submit"}
                                                disabled={!formRenderProps.allowSubmit}
                                            >
                                                Login
                                            </Button>

                                            <Button
                                                iconClass="fa-solid fa-fingerprint"
                                                fillMode="flat"
                                                type="button"
                                                onClick={validateLoginKey} />
                                        </div>
                                        <div className="mt-3 noFormField">
                                            <Link to="/forgotPassword">I forgot my password</Link>                                        
                                        </div>
                                    </FormElement>
                                )}
                            />
                        :
                            <>
                                {!isTwoFactorAuthenticationSetUp &&
                                    <div className="mb-3">
                                        <h4>
                                            You must set up multi-factor authentication. 
                                        </h4>
                                        <div>
                                            Please scan the below QR code with your authenticator app.
                                        </div>
                                        <div className="text-center">
                                            <img src={qrCodeSetupImageUrl} alt="QR Code" title="Scan this QR code" />
                                        </div>
                                    </div>
                                }

                                <Form
                                    onSubmit={twoFactorAuthenticationFormSubmitted}
                                    render={(formRenderProps) => (
                                        <FormElement>                                    
                                            <fieldset className={"k-form-fieldset"}>   
                                                <Field
                                                    id={"twoFactorAuthenticationCode"}
                                                    name={"twoFactorAuthenticationCode"}
                                                    type={"text"}
                                                    component={FormInput}
                                                    validator={requiredValidator}
                                                    label={twoFactorAuthenticationTypeId === TwoFactorAuthenticationType.Authenticator ? "Enter the code from your authenticator app" : "Enter the passcode sent to your email address"}
                                                    aria-label={twoFactorAuthenticationTypeId === TwoFactorAuthenticationType.Authenticator ? "Enter the code from your authenticator app" : "Enter the passcode sent to your email address"}
                                                    className="twoFactorAuthenticationCode"
                                                />
                                            </fieldset>
                                            <div className="k-form-buttons">
                                                <Button
                                                    themeColor={"primary"}
                                                    type={"submit"}
                                                    disabled={!formRenderProps.allowSubmit}
                                                >
                                                    Submit
                                                </Button>
                                            </div>
                                        </FormElement>
                                    )}
                                />
                            </>                            
                        }
                    </Dialog>
                )
            }
        </>
    );
}